'use client';

import { useRef } from 'react';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';

import { Box, Button, Stack, Typography } from '@mui/material';

import FilePreview from '@/components/Forms/FilePreview';
import Plus from '@/components/SvgIcons/Plus';
import colors from '@/styles/themes/colors';

import styles from './FormFileUpload.module.scss';

export interface FormFileUploadProps {
  name: ControllerProps['name'];
  label: string;
  accept?: string;
  maxSize?: number;
}

const FormFileUpload = ({ name, label, accept = '.pdf,.doc,.docx', maxSize = 5 }: FormFileUploadProps) => {
  const { control, setValue, watch } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const file = watch(name) as File | null;

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile) {
      const fileSizeMB = selectedFile.size / (1024 * 1024);

      if (fileSizeMB > maxSize) {
        return;
      }

      setValue(name, selectedFile);
    }
  };

  const handleRemove = () => {
    setValue(name, null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const render: ControllerProps['render'] = () => (
    <Stack className={file ? styles.containerWithFile : styles.container}>
      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} className={styles.hiddenInput} />
      {file ? (
        <FilePreview file={file} onRemove={handleRemove} />
      ) : (
        <Button variant="outlined" onClick={handleClick} className={styles.uploadButton}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" color={colors.white}>
              {label}
            </Typography>
            <Box component="span" className={styles.iconWrapper}>
              <Plus fill={colors.white} size={16} />
            </Box>
          </Stack>
        </Button>
      )}
    </Stack>
  );

  return <Controller name={name} control={control} render={render} />;
};

export default FormFileUpload;
